/**
 * ProgressBar - Linear progress indicator
 *
 * Usage:
 *   <ProgressBar
 *     data={{
 *       current: 3,
 *       target: 5,
 *       label: 'Tasks completed'
 *     }}
 *   />
 */

import type { BlockProps } from './types';
import './blocks.css';

export interface ProgressBarData {
  /** Current value */
  current: number;
  /** Target value */
  target: number;
  /** Label */
  label?: string;
  /** Show as percentage or fraction */
  displayMode?: 'percentage' | 'fraction' | 'value';
}

export interface ProgressBarProps extends BlockProps<ProgressBarData> {
  /** Bar height */
  height?: number;
  /** Show value inside bar */
  showValueInside?: boolean;
  /** Color when complete */
  completeColor?: string;
  /** Striped animation */
  animated?: boolean;
}

export function ProgressBar({
  data,
  title,
  height = 24,
  showValueInside = false,
  completeColor = 'var(--color-success)',
  animated = false,
  className,
}: ProgressBarProps) {
  const { current: rawCurrent, target: rawTarget, label, displayMode = 'percentage' } = data;

  // Guard against undefined/null values from LLM-generated card data
  const current = rawCurrent ?? 0;
  const target = rawTarget ?? 0;

  // Guard against division by zero - treat target=0 as 0% progress
  const percentage = target === 0 ? 0 : Math.min(100, Math.max(0, (current / target) * 100));
  const isComplete = percentage >= 100;

  const getDisplayValue = () => {
    switch (displayMode) {
      case 'fraction':
        return `${current} / ${target}`;
      case 'value':
        return current.toLocaleString();
      case 'percentage':
      default:
        return `${percentage.toFixed(0)}%`;
    }
  };

  return (
    <div className={`progress-bar-block ${className || ''}`}>
      {(title || label) && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-2)',
        }}>
          <span style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 500,
            color: 'var(--color-text)',
          }}>
            {title || label}
          </span>
          {!showValueInside && (
            <span style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 600,
              color: isComplete ? completeColor : 'var(--color-text-subtle)',
            }}>
              {getDisplayValue()}
            </span>
          )}
        </div>
      )}

      <div style={{
        width: '100%',
        height,
        backgroundColor: 'var(--color-surface-muted)',
        borderRadius: height / 2,
        overflow: 'hidden',
        position: 'relative',
      }}>
        <div
          style={{
            height: '100%',
            width: `${percentage}%`,
            backgroundColor: isComplete ? completeColor : 'var(--color-primary)',
            borderRadius: height / 2,
            transition: 'width 0.3s ease',
            backgroundImage: animated && !isComplete
              ? 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)'
              : undefined,
            backgroundSize: animated ? '1rem 1rem' : undefined,
            animation: animated && !isComplete ? 'progress-bar-stripes 1s linear infinite' : undefined,
          }}
        />

        {showValueInside && (
          <span style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '12px',
            fontWeight: 600,
            color: percentage > 50 ? '#fff' : 'var(--color-text)',
          }}>
            {getDisplayValue()}
          </span>
        )}
      </div>

      <style>{`
        @keyframes progress-bar-stripes {
          from { background-position: 1rem 0; }
          to { background-position: 0 0; }
        }
      `}</style>
    </div>
  );
}

export default ProgressBar;
